/**
 * Mock Data for Coming Soon Pages
 * This file contains realistic enterprise GRC (Governance, Risk, and Compliance) data
 * for demonstrating page functionality while features are under development.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type RiskSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type RiskStatus = 'Open' | 'Mitigated' | 'Closed';
export type RiskCategory = 'Operational' | 'Financial' | 'Strategic' | 'Compliance' | 'Technology' | 'Cybersecurity' | 'Reputational';

export type ControlStatus = 'Active' | 'Inactive' | 'Under Review' | 'Pending';
export type ComplianceFramework = 'SOC2' | 'ISO27001' | 'NIST' | 'GDPR' | 'DORA' | 'Basel III';

export type AuditStatus = 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled';

export interface RiskItem {
  id: string;
  title: string;
  category: RiskCategory;
  severity: RiskSeverity;
  owner: string;
  status: RiskStatus;
  last_updated: string;
  description?: string;
  inherent_risk: number;
  residual_risk: number;
}

export interface ControlItem {
  control_id: string;
  name: string;
  description: string;
  framework: ComplianceFramework[];
  status: ControlStatus;
  effectiveness: number;
  owner: string;
  last_review: string;
  next_review: string;
}

export interface AuditEvent {
  id: string;
  title: string;
  date: string;
  status: AuditStatus;
  auditor: string;
  scope: string;
  outcome?: string;
  findings?: number;
}

export interface ComplianceMetric {
  framework: string;
  score: number;
  controls_total: number;
  controls_compliant: number;
  last_assessment: string;
}

export interface TopRisk {
  id: string;
  title: string;
  severity: RiskSeverity;
  score: number;
}

export interface RecentActivity {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  resource: string;
}

// ============================================================================
// COMPLIANCE DASHBOARD MOCK DATA
// ============================================================================

export const complianceDashboardData = {
  overallScore: 87,
  activeControls: 156,
  openFindings: 12,
  upcomingAudits: 3,
  
  topRisks: [
    { id: 'R-001', title: 'Data Breach via Third-Party Vendor', severity: 'Critical' as RiskSeverity, score: 9.2 },
    { id: 'R-015', title: 'API Rate Limiting Failure', severity: 'High' as RiskSeverity, score: 7.8 },
    { id: 'R-023', title: 'Insufficient Backup Recovery Testing', severity: 'High' as RiskSeverity, score: 7.5 },
    { id: 'R-042', title: 'Outdated Encryption Protocols', severity: 'Medium' as RiskSeverity, score: 6.2 },
    { id: 'R-067', title: 'Employee Security Training Gap', severity: 'Medium' as RiskSeverity, score: 5.8 },
  ] as TopRisk[],
  
  recentAudits: [
    {
      id: 'A-2024-Q3-001',
      title: 'SOC 2 Type II Annual Audit',
      date: '2024-09-15',
      status: 'Completed' as AuditStatus,
      auditor: 'External - CyberAudit LLC',
      scope: 'Security, Availability, Processing Integrity',
      outcome: 'Clean Opinion',
      findings: 2,
    },
    {
      id: 'A-2024-Q3-002',
      title: 'ISO 27001 Surveillance Audit',
      date: '2024-10-02',
      status: 'Completed' as AuditStatus,
      auditor: 'External - ISO Certification Body',
      scope: 'Information Security Management System',
      outcome: 'Certificate Maintained',
      findings: 5,
    },
    {
      id: 'A-2024-Q4-001',
      title: 'GDPR Compliance Review',
      date: '2024-11-20',
      status: 'Scheduled' as AuditStatus,
      auditor: 'External - DataPrivacy Partners',
      scope: 'Data Processing Activities',
    },
  ] as AuditEvent[],
  
  recentActivity: [
    { id: '1', action: 'Updated control', user: 'Sarah Chen', timestamp: '2 hours ago', resource: 'Access Control Policy' },
    { id: '2', action: 'Completed audit', user: 'Michael Torres', timestamp: '5 hours ago', resource: 'Q3 SOC2 Audit' },
    { id: '3', action: 'Risk assessment', user: 'Emily Rodriguez', timestamp: '1 day ago', resource: 'Third-Party Risk R-001' },
    { id: '4', action: 'Policy approved', user: 'David Kim', timestamp: '2 days ago', resource: 'Incident Response Plan v2.1' },
    { id: '5', action: 'Control tested', user: 'Jessica Martinez', timestamp: '3 days ago', resource: 'MFA Implementation' },
  ] as RecentActivity[],
  
  complianceByFramework: [
    { framework: 'SOC2', score: 92, controls_total: 64, controls_compliant: 59, last_assessment: '2024-09-15' },
    { framework: 'ISO27001', score: 88, controls_total: 114, controls_compliant: 100, last_assessment: '2024-10-02' },
    { framework: 'GDPR', score: 85, controls_total: 42, controls_compliant: 36, last_assessment: '2024-08-10' },
    { framework: 'NIST CSF', score: 81, controls_total: 98, controls_compliant: 79, last_assessment: '2024-07-22' },
  ] as ComplianceMetric[],
};

// ============================================================================
// RISK REGISTER MOCK DATA
// ============================================================================

export const riskRegisterData: RiskItem[] = [
  {
    id: 'R-001',
    title: 'Data Breach via Third-Party Vendor',
    category: 'Cybersecurity',
    severity: 'Critical',
    owner: 'Sarah Chen',
    status: 'Open',
    last_updated: '2024-11-01',
    description: 'Potential data exposure through inadequate vendor security controls',
    inherent_risk: 9.2,
    residual_risk: 7.1,
  },
  {
    id: 'R-015',
    title: 'API Rate Limiting Failure',
    category: 'Technology',
    severity: 'High',
    owner: 'Michael Torres',
    status: 'Mitigated',
    last_updated: '2024-10-28',
    description: 'API endpoints vulnerable to DDoS attacks due to insufficient rate limiting',
    inherent_risk: 7.8,
    residual_risk: 3.2,
  },
  {
    id: 'R-023',
    title: 'Insufficient Backup Recovery Testing',
    category: 'Operational',
    severity: 'High',
    owner: 'Emily Rodriguez',
    status: 'Open',
    last_updated: '2024-10-25',
    description: 'Backup recovery procedures not regularly tested for critical systems',
    inherent_risk: 7.5,
    residual_risk: 6.8,
  },
  {
    id: 'R-042',
    title: 'Outdated Encryption Protocols',
    category: 'Technology',
    severity: 'Medium',
    owner: 'David Kim',
    status: 'Open',
    last_updated: '2024-10-22',
    description: 'Legacy systems using TLS 1.1 instead of TLS 1.3',
    inherent_risk: 6.2,
    residual_risk: 5.9,
  },
  {
    id: 'R-067',
    title: 'Employee Security Training Gap',
    category: 'Compliance',
    severity: 'Medium',
    owner: 'Jessica Martinez',
    status: 'Mitigated',
    last_updated: '2024-10-20',
    description: '23% of employees have not completed annual security awareness training',
    inherent_risk: 5.8,
    residual_risk: 2.1,
  },
  {
    id: 'R-089',
    title: 'Cloud Cost Overrun Risk',
    category: 'Financial',
    severity: 'Medium',
    owner: 'Robert Chang',
    status: 'Open',
    last_updated: '2024-10-18',
    description: 'Unmonitored cloud resource usage leading to budget overruns',
    inherent_risk: 5.5,
    residual_risk: 5.0,
  },
  {
    id: 'R-102',
    title: 'Supply Chain Disruption',
    category: 'Strategic',
    severity: 'High',
    owner: 'Amanda Lee',
    status: 'Open',
    last_updated: '2024-10-15',
    description: 'Single source dependency for critical infrastructure components',
    inherent_risk: 7.2,
    residual_risk: 6.5,
  },
  {
    id: 'R-134',
    title: 'Regulatory Compliance Deadline',
    category: 'Compliance',
    severity: 'Critical',
    owner: 'Thomas Wright',
    status: 'Open',
    last_updated: '2024-10-12',
    description: 'DORA compliance requirements due Q1 2025 with significant gaps',
    inherent_risk: 8.9,
    residual_risk: 7.8,
  },
  {
    id: 'R-156',
    title: 'Key Personnel Dependency',
    category: 'Operational',
    severity: 'Medium',
    owner: 'Linda Park',
    status: 'Mitigated',
    last_updated: '2024-10-10',
    description: 'Critical systems knowledge concentrated in single team member',
    inherent_risk: 6.0,
    residual_risk: 3.5,
  },
  {
    id: 'R-178',
    title: 'Reputational Risk from Service Outages',
    category: 'Reputational',
    severity: 'Low',
    owner: 'Carlos Hernandez',
    status: 'Closed',
    last_updated: '2024-10-05',
    description: 'Improved monitoring and redundancy implemented',
    inherent_risk: 4.2,
    residual_risk: 1.5,
  },
];

// ============================================================================
// CONTROLS LIBRARY MOCK DATA
// ============================================================================

export const controlsLibraryData: ControlItem[] = [
  {
    control_id: 'AC-001',
    name: 'Multi-Factor Authentication (MFA)',
    description: 'Mandatory MFA for all user accounts accessing production systems',
    framework: ['SOC2', 'ISO27001', 'NIST'],
    status: 'Active',
    effectiveness: 95,
    owner: 'Sarah Chen',
    last_review: '2024-10-15',
    next_review: '2025-01-15',
  },
  {
    control_id: 'AC-002',
    name: 'Role-Based Access Control (RBAC)',
    description: 'Granular permissions based on job function and least privilege principle',
    framework: ['SOC2', 'ISO27001', 'GDPR'],
    status: 'Active',
    effectiveness: 88,
    owner: 'Michael Torres',
    last_review: '2024-09-20',
    next_review: '2024-12-20',
  },
  {
    control_id: 'DLP-001',
    name: 'Data Loss Prevention',
    description: 'Automated scanning and blocking of sensitive data in outbound communications',
    framework: ['GDPR', 'SOC2'],
    status: 'Active',
    effectiveness: 82,
    owner: 'Emily Rodriguez',
    last_review: '2024-10-01',
    next_review: '2025-01-01',
  },
  {
    control_id: 'EN-001',
    name: 'Data Encryption at Rest',
    description: 'AES-256 encryption for all data stored in databases and file systems',
    framework: ['SOC2', 'ISO27001', 'GDPR', 'NIST'],
    status: 'Active',
    effectiveness: 98,
    owner: 'David Kim',
    last_review: '2024-10-10',
    next_review: '2025-04-10',
  },
  {
    control_id: 'EN-002',
    name: 'Data Encryption in Transit',
    description: 'TLS 1.3 for all network communications',
    framework: ['SOC2', 'ISO27001', 'NIST'],
    status: 'Under Review',
    effectiveness: 91,
    owner: 'David Kim',
    last_review: '2024-10-05',
    next_review: '2024-11-15',
  },
  {
    control_id: 'VM-001',
    name: 'Vulnerability Management Program',
    description: 'Weekly automated vulnerability scans with 30-day remediation SLA',
    framework: ['ISO27001', 'NIST'],
    status: 'Active',
    effectiveness: 85,
    owner: 'Jessica Martinez',
    last_review: '2024-09-25',
    next_review: '2024-12-25',
  },
  {
    control_id: 'IR-001',
    name: 'Incident Response Plan',
    description: 'Documented procedures for security incident detection, response, and recovery',
    framework: ['SOC2', 'ISO27001', 'NIST', 'DORA'],
    status: 'Active',
    effectiveness: 90,
    owner: 'Robert Chang',
    last_review: '2024-10-12',
    next_review: '2025-04-12',
  },
  {
    control_id: 'BC-001',
    name: 'Business Continuity Plan',
    description: 'Comprehensive BCP with RTO/RPO targets and annual testing',
    framework: ['ISO27001', 'DORA', 'Basel III'],
    status: 'Active',
    effectiveness: 87,
    owner: 'Amanda Lee',
    last_review: '2024-09-30',
    next_review: '2025-03-30',
  },
  {
    control_id: 'LOG-001',
    name: 'Security Event Logging',
    description: 'Centralized logging with 90-day retention and real-time alerting',
    framework: ['SOC2', 'ISO27001', 'NIST'],
    status: 'Active',
    effectiveness: 93,
    owner: 'Thomas Wright',
    last_review: '2024-10-08',
    next_review: '2025-01-08',
  },
  {
    control_id: 'CHG-001',
    name: 'Change Management Process',
    description: 'Formal approval and testing requirements for production changes',
    framework: ['SOC2', 'ISO27001', 'DORA'],
    status: 'Active',
    effectiveness: 89,
    owner: 'Linda Park',
    last_review: '2024-09-15',
    next_review: '2024-12-15',
  },
  {
    control_id: 'VEN-001',
    name: 'Third-Party Risk Assessment',
    description: 'Annual vendor security assessments for all critical suppliers',
    framework: ['SOC2', 'GDPR', 'DORA'],
    status: 'Active',
    effectiveness: 78,
    owner: 'Carlos Hernandez',
    last_review: '2024-10-20',
    next_review: '2025-04-20',
  },
  {
    control_id: 'TR-001',
    name: 'Security Awareness Training',
    description: 'Annual mandatory training with quarterly phishing simulations',
    framework: ['SOC2', 'ISO27001', 'GDPR'],
    status: 'Active',
    effectiveness: 81,
    owner: 'Jessica Martinez',
    last_review: '2024-10-01',
    next_review: '2025-01-01',
  },
  {
    control_id: 'BK-001',
    name: 'Automated Backup System',
    description: 'Daily automated backups with monthly recovery testing',
    framework: ['ISO27001', 'DORA', 'Basel III'],
    status: 'Active',
    effectiveness: 94,
    owner: 'Emily Rodriguez',
    last_review: '2024-10-18',
    next_review: '2025-04-18',
  },
  {
    control_id: 'PEN-001',
    name: 'Penetration Testing',
    description: 'Annual third-party penetration testing of all external-facing systems',
    framework: ['SOC2', 'ISO27001', 'NIST'],
    status: 'Pending',
    effectiveness: 0,
    owner: 'Sarah Chen',
    last_review: '2024-01-15',
    next_review: '2024-12-01',
  },
  {
    control_id: 'PRIV-001',
    name: 'Privacy Impact Assessments',
    description: 'Mandatory PIA for all new data processing activities',
    framework: ['GDPR'],
    status: 'Active',
    effectiveness: 86,
    owner: 'Amanda Lee',
    last_review: '2024-09-28',
    next_review: '2025-03-28',
  },
];

// ============================================================================
// AUDIT TIMELINE MOCK DATA
// ============================================================================

export const auditTimelineData: AuditEvent[] = [
  {
    id: 'A-2024-Q4-001',
    title: 'GDPR Compliance Review',
    date: '2024-11-20',
    status: 'Scheduled',
    auditor: 'External - DataPrivacy Partners',
    scope: 'Data Processing Activities, Privacy Controls, DPIA Review',
  },
  {
    id: 'A-2024-Q4-002',
    title: 'DORA Readiness Assessment',
    date: '2024-12-05',
    status: 'Scheduled',
    auditor: 'Internal - Risk Team',
    scope: 'ICT Risk Management, Incident Response, Third-Party Dependencies',
  },
  {
    id: 'A-2025-Q1-001',
    title: 'SOC 2 Type II Annual Audit',
    date: '2025-01-15',
    status: 'Scheduled',
    auditor: 'External - CyberAudit LLC',
    scope: 'Security, Availability, Confidentiality Controls',
  },
  {
    id: 'A-2024-Q3-002',
    title: 'ISO 27001 Surveillance Audit',
    date: '2024-10-02',
    status: 'Completed',
    auditor: 'External - ISO Certification Body',
    scope: 'Information Security Management System',
    outcome: 'Certificate Maintained - 5 Minor Findings',
    findings: 5,
  },
  {
    id: 'A-2024-Q3-001',
    title: 'SOC 2 Type II Annual Audit',
    date: '2024-09-15',
    status: 'Completed',
    auditor: 'External - CyberAudit LLC',
    scope: 'Security, Availability, Processing Integrity',
    outcome: 'Clean Opinion - 2 Management Points',
    findings: 2,
  },
  {
    id: 'A-2024-Q2-003',
    title: 'Vendor Security Assessment - AWS',
    date: '2024-08-10',
    status: 'Completed',
    auditor: 'Internal - Vendor Management',
    scope: 'Third-Party Cloud Infrastructure Controls',
    outcome: 'Approved - No Issues',
    findings: 0,
  },
  {
    id: 'A-2024-Q2-002',
    title: 'Internal Controls Testing',
    date: '2024-07-22',
    status: 'Completed',
    auditor: 'Internal - Compliance Team',
    scope: 'Access Controls, Change Management, Logging',
    outcome: 'Effective - 3 Recommendations',
    findings: 3,
  },
  {
    id: 'A-2024-Q2-001',
    title: 'Penetration Testing',
    date: '2024-06-15',
    status: 'Completed',
    auditor: 'External - RedTeam Security',
    scope: 'External Attack Surface, Web Applications, APIs',
    outcome: 'Low Risk - All Issues Remediated',
    findings: 8,
  },
];
